import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useMemo } from 'react';

import { createHistogram, maxNumberValue, normalizeValue } from './utils';

export const StyledHistogram = styled('svg', {
  shouldForwardProp: (prop) => prop !== 'height',
})<{ height?: number | string }>`
  display: block;
  width: 100%;
  max-height: ${({ height }) => (typeof height === 'number' ? `${height}px` : height)};
  background: ${({ theme }) => theme.palette.colors.white};

  rect {
    fill: ${({ theme }) => theme.palette.colors.gray[300]};
    stroke: ${({ theme }) => theme.palette.colors.white};
    stroke-width: 0.1;
    opacity: 0.3;
  }
`;

const withinRangeStyles = css`
  ${StyledHistogram} & {
    opacity: 1;
  }
`;

export type HistogramProps = {
  highlightStart?: number;
  highlightEnd?: number;
  data: number[];
  height?: number;
};

export const Histogram = ({
  highlightStart,
  highlightEnd,
  data,
  height = 200,
}: HistogramProps): JSX.Element => {
  const { histogram, maxValueData, maxValueHistogram, columnWidth } = useMemo(() => {
    const histogram = createHistogram(data);
    return {
      histogram,
      maxValueData: maxNumberValue(data),
      maxValueHistogram: maxNumberValue(histogram),
      columnWidth: 100 / histogram.length,
    };
  }, [data]);

  const normalizedHighlightStart =
    highlightStart !== undefined ? normalizeValue(highlightStart, maxValueData) : 0;
  const normalizedHighlightEnd =
    highlightEnd !== undefined ? normalizeValue(highlightEnd, maxValueData) : maxValueData;

  return (
    <StyledHistogram
      height={height}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {histogram.map((value, index) => {
        const xPosition = index * columnWidth;
        const columnHeight = (value / maxValueHistogram) * 100;
        const valueIsWithinRange =
          xPosition >= normalizedHighlightStart &&
          index * columnWidth <= normalizedHighlightEnd - columnWidth;
        return (
          <rect
            x={xPosition}
            y={100 - columnHeight}
            width={columnWidth}
            height={columnHeight}
            key={`${value}_${index}`}
            css={valueIsWithinRange && withinRangeStyles}
          />
        );
      })}
    </StyledHistogram>
  );
};
